/**
 * **Signing out of Boone in a browser** — one function, because both first-party SPAs offer
 * it and the posture below is a safety property rather than a preference.
 *
 * It began in `apps/login.iamboone.app/src/session.ts` for #104, where an invitee arriving in
 * a browser holding somebody else's session needs a way to leave it. The control panel (#301)
 * needs the same thing more sharply: it is the surface that renders family memory, and the
 * Stytch cookie is shared across a family laptop.
 *
 * **It is here for {@link STYTCH_CLIENT_OPTIONS}'s reason, one step on.** Both apps write
 * that cookie, so both must agree on how it is scoped; both apps now *clear* it, so both must
 * agree on what happens when clearing fails. A second copy of `forceClear` is a copy somebody
 * can drop, and the result — a member who pressed "sign out", saw it succeed, and is still
 * signed in — breaks no build and appears only on the machine it matters on.
 *
 * It reaches nothing: no SDK import, no network, no environment. The client is a **structural**
 * parameter naming exactly the one capability this uses, which `useStytch()`'s client
 * satisfies, so this package keeps its "no runtime dependency of its own" rule.
 */

/**
 * The one thing sign-out needs from a Stytch client. Structural on purpose: it states the
 * capability rather than importing a vendor type into a package that depends on nothing.
 */
export interface SessionRevoker {
  session: { revoke(options?: { forceClear?: boolean }): Promise<unknown> };
}

/**
 * End the browser's Stytch session.
 *
 * **The scope is the whole browser, and it cannot be anything else.** The session is a cookie
 * on `.iamboone.app`, shared by every route, tab and first-party host; there is no per-tab or
 * per-app session to revoke. So signing out of the control panel also ends a `/authorize`
 * flow open in another tab — every surface that offers this says so rather than pretending
 * otherwise.
 *
 * `forceClear` drops the local session even if the revoke call cannot reach Stytch, so a
 * network failure can never strand somebody under an identity they have rejected. That is a
 * UI decision and never a server one: whatever the browser believes, a membership write is
 * still refused on its own terms.
 */
export async function endSession(client: SessionRevoker): Promise<void> {
  try {
    await client.session.revoke({ forceClear: true });
  } catch {
    // Deliberately swallowed: `forceClear` has already cleared the local session, which is
    // what the caller is waiting on. Surfacing a vendor error would say nothing the person
    // could act on, and both SPAs' error copy is generic for that reason.
  }
}
